import {
  WeatherConfigSchema,
  WeatherDataSchema,
  weatherDescription,
  type WeatherConfig,
} from '@hud/core';
import { registerWidget, type WidgetProps } from '../registry.js';
import { formatAge, useSourceData } from '../hooks/useSourceData.js';
import './weather.css';

/**
 * Current conditions from a scheduled `weather` source.
 *
 * Reads `data/<source>/latest.json` out of the store rather than calling an
 * API, so it renders offline and no key exists in the browser to leak.
 */
function Weather({ config }: WidgetProps<WeatherConfig>) {
  const state = useSourceData(config.source, WeatherDataSchema, config.staleAfterMinutes);

  if (state.status === 'missing') {
    return (
      <div class="hud-weather hud-weather--empty">
        <div class="hud-weather__note">Waiting for the first fetch</div>
        <div class="hud-weather__path">{state.path}</div>
      </div>
    );
  }

  if (state.status === 'invalid') {
    return (
      <div class="hud-weather hud-weather--empty">
        <div class="hud-weather__note">Unreadable data</div>
        <div class="hud-weather__path">{state.error}</div>
      </div>
    );
  }

  const { data, ageMinutes, stale } = state;

  return (
    <div class="hud-weather">
      <div class="hud-weather__main">
        <span class="hud-weather__temp">
          {Math.round(data.current.temp)}
          <span class="hud-weather__unit">°{data.unit}</span>
        </span>
        <span class="hud-weather__conditions">{weatherDescription(data.current.code)}</span>
      </div>

      <div class="hud-weather__meta">
        <span class="hud-weather__place">{data.label}</span>
        {config.showHighLow ? (
          <span class="hud-weather__range">
            ↑{Math.round(data.today.high)}° ↓{Math.round(data.today.low)}°
          </span>
        ) : null}
      </div>

      {/*
        Always shown, not just when stale. A scheduled fetch fails by leaving
        yesterday's number in place looking perfectly current, so the age is
        part of the reading rather than an error state.
      */}
      <div class={stale ? 'hud-weather__age hud-weather__age--stale' : 'hud-weather__age'}>
        {formatAge(ageMinutes)}
      </div>
    </div>
  );
}

registerWidget({
  type: 'weather',
  schema: WeatherConfigSchema,
  component: Weather,
});
